export function normalizeCommerceQuantity(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.floor(parsed));
}

export function commerceTierForQuantity(value) {
  const quantity = normalizeCommerceQuantity(value);
  if (quantity >= 3) return { quantity: 3, percentage: 20 };
  if (quantity === 2) return { quantity: 2, percentage: 10 };
  return null;
}

export function discountedTotalCents(unitPriceCents, quantity, percentage) {
  const subtotal = Number(unitPriceCents) * normalizeCommerceQuantity(quantity);
  return Math.round(subtotal * (1 - Number(percentage) / 100));
}

export function directCheckoutRequest(variantId, quantity) {
  const normalizedVariantId = Number(variantId);
  if (!Number.isInteger(normalizedVariantId) || normalizedVariantId <= 0) {
    throw new TypeError('È necessario selezionare una variante valida.');
  }

  return {
    endpoint: '/cart/add.js',
    redirect: '/checkout',
    payload: {
      items: [{ id: normalizedVariantId, quantity: normalizeCommerceQuantity(quantity) }],
    },
  };
}

export function stickySizeOptionForValue(options, value) {
  const normalizedValue = String(value ?? '').trim().toLocaleUpperCase('it-IT');
  const availableOptions = options.filter((option) => option.available !== false);
  return availableOptions.find((option) => String(option.value).trim().toLocaleUpperCase('it-IT') === normalizedValue)
    || availableOptions[0]
    || options[0]
    || null;
}

export function reviewVisibleCount(total, expanded, initial = 8) {
  const normalizedTotal = Math.max(0, Math.floor(Number(total) || 0));
  const normalizedInitial = Math.max(0, Math.floor(Number(initial) || 0));
  return expanded ? normalizedTotal : Math.min(normalizedTotal, normalizedInitial);
}

export function reviewToggleLabel(expanded) {
  return expanded ? 'Mostra meno' : 'Mostra altro';
}

export function stickyBarShouldForceVisible(buyButtonsBottom) {
  const normalizedBottom = Number(buyButtonsBottom);
  return Number.isFinite(normalizedBottom) && normalizedBottom <= 0;
}

export function stickyVisibilityTargets(doc, viewport) {
  const targets = [viewport];
  const pageWrapper = doc?.querySelector?.('.page-wrapper');
  if (pageWrapper && pageWrapper !== viewport) targets.push(pageWrapper);
  return targets.filter(Boolean);
}

function productFormFor(root) {
  const details = root.closest('.product-details') || root.closest('.product-information') || document;
  return details.querySelector('product-form-component form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
}

function setNativeQuantity(root, value) {
  const quantity = normalizeCommerceQuantity(value);
  const form = productFormFor(root);
  const input = form?.querySelector('input[name="quantity"]');

  if (input) {
    input.value = String(quantity);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  const output = root.querySelector('[data-av-quantity-output]');
  if (output) output.textContent = String(quantity);

  const tier = commerceTierForQuantity(quantity);
  root.querySelectorAll('[data-av-volume]').forEach((card) => {
    const selected = Number(card.dataset.avVolume) === tier?.quantity;
    card.classList.toggle('is-selected', selected);
    card.setAttribute('aria-checked', String(selected));
  });

  root.dataset.quantity = String(quantity);
  return quantity;
}

function openSizeGuide(root) {
  const dialog = root.querySelector('[data-av-size-dialog]');
  if (!dialog) return;
  if (typeof dialog.showModal === 'function') dialog.showModal();
  else dialog.setAttribute('open', '');
}

function closeSizeGuide(root) {
  const dialog = root.querySelector('[data-av-size-dialog]');
  if (!dialog) return;
  if (typeof dialog.close === 'function') dialog.close();
  else dialog.removeAttribute('open');
}

async function purchaseNow(root, button) {
  const form = productFormFor(root);
  const variantId = form?.querySelector('[name="id"]')?.value;
  const quantity = form?.querySelector('[name="quantity"]')?.value || root.dataset.quantity || 1;

  try {
    const request = directCheckoutRequest(variantId, quantity);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const response = await fetch(request.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(request.payload),
    });
    if (!response.ok) throw new Error('Impossibile avviare il checkout.');

    window.location.assign(request.redirect);
  } catch (error) {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    const message = root.querySelector('[data-av-buy-now-error]');
    if (message) {
      message.textContent = error instanceof Error ? error.message : 'Impossibile avviare il checkout.';
      message.hidden = false;
    }
  }
}

function initBuyNowButton(root) {
  const button = root.querySelector('[data-av-buy-now]');
  const form = productFormFor(root);
  const buttonStack = form?.querySelector('.product-form-buttons');
  const addToCart = buttonStack?.querySelector('.add-to-cart-button');
  if (!button || !buttonStack || !addToCart) return;

  if (button.dataset.avBuyNowReady !== 'true') {
    button.dataset.avBuyNowReady = 'true';
    button.addEventListener('click', () => purchaseNow(root, button));
  }

  addToCart.insertAdjacentElement('afterend', button);
  button.hidden = false;
}

export function initPdpCommerce(root) {
  if (!root || root.dataset.avCommerceReady === 'true') return;
  root.dataset.avCommerceReady = 'true';

  root.querySelector('[data-av-quantity-minus]')?.addEventListener('click', () => {
    setNativeQuantity(root, Number(root.dataset.quantity || 1) - 1);
  });
  root.querySelector('[data-av-quantity-plus]')?.addEventListener('click', () => {
    setNativeQuantity(root, Number(root.dataset.quantity || 1) + 1);
  });
  root.querySelectorAll('[data-av-volume]').forEach((card) => {
    card.addEventListener('click', () => setNativeQuantity(root, Number(card.dataset.avVolume)));
  });
  root.querySelector('[data-av-size-open]')?.addEventListener('click', () => openSizeGuide(root));
  root.querySelector('[data-av-size-close]')?.addEventListener('click', () => closeSizeGuide(root));
  root.querySelector('[data-av-size-dialog]')?.addEventListener('click', (event) => {
    if (event.target === event.currentTarget) closeSizeGuide(root);
  });

  setNativeQuantity(root, 1);
  initBuyNowButton(root);
}

function initAllPdpCommerce(scope = document) {
  scope.querySelectorAll('[data-av-commerce]').forEach(initPdpCommerce);
}

function setReviewsExpanded(root, expanded) {
  const cards = Array.from(root.querySelectorAll('.av-review-card'));
  const visibleCount = reviewVisibleCount(cards.length, expanded);

  cards.forEach((card, index) => {
    card.hidden = index >= visibleCount;
  });

  root.querySelectorAll('[data-av-review-pair]').forEach((pair) => {
    pair.hidden = Array.from(pair.querySelectorAll('.av-review-card')).every((card) => card.hidden);
  });

  const moreButton = root.querySelector('[data-av-reviews-more]');
  if (moreButton) {
    moreButton.hidden = cards.length <= reviewVisibleCount(cards.length, false);
    moreButton.textContent = reviewToggleLabel(expanded);
    moreButton.setAttribute('aria-expanded', String(expanded));
  }
  root.dataset.avReviewsExpanded = String(expanded);
}

function openReviewDialog(root) {
  const dialog = root.querySelector('[data-av-review-dialog]');
  if (!dialog) return;
  if (typeof dialog.showModal === 'function') dialog.showModal();
  else dialog.setAttribute('open', '');
}

function closeReviewDialog(root) {
  const dialog = root.querySelector('[data-av-review-dialog]');
  if (!dialog) return;
  if (typeof dialog.close === 'function') dialog.close();
  else dialog.removeAttribute('open');
}

function initReviewSection(root) {
  if (!root || root.dataset.avReviewsReady === 'true') return;
  root.dataset.avReviewsReady = 'true';

  root.querySelector('[data-av-reviews-more]')?.addEventListener('click', () => {
    setReviewsExpanded(root, root.dataset.avReviewsExpanded !== 'true');
  });
  root.querySelector('[data-av-review-open]')?.addEventListener('click', () => openReviewDialog(root));
  root.querySelector('[data-av-review-close]')?.addEventListener('click', () => closeReviewDialog(root));
  root.querySelector('[data-av-review-dialog]')?.addEventListener('click', (event) => {
    if (event.target === event.currentTarget) closeReviewDialog(root);
  });

  setReviewsExpanded(root, false);
  if (root.querySelector('[data-av-review-success], [data-av-review-errors]')) openReviewDialog(root);
}

function initAllReviewSections(scope = document) {
  scope.querySelectorAll('[data-av-reviews]').forEach(initReviewSection);
}

function stickySizeInputs() {
  return Array.from(document.querySelectorAll('variant-picker input[type="radio"][data-option-name]'))
    .filter((input) => String(input.dataset.optionName).trim().toLocaleLowerCase('it-IT') === 'taglia');
}

function setStickyButtonText(bar) {
  const content = bar.querySelector('.sticky-add-to-cart__button .add-to-cart-text__content');
  const target = content?.querySelector('span:first-child') || content;
  if (target && target.textContent.trim() !== 'PREORDINA ORA') {
    target.textContent = 'PREORDINA ORA';
  }
}

function syncStickySize(select, inputs) {
  const checked = inputs.find((input) => input.checked);
  const options = inputs.map((input) => ({
    value: input.value,
    available: !input.disabled && input.dataset.available !== 'false',
  }));
  const selected = stickySizeOptionForValue(options, checked?.value);
  if (selected) select.value = selected.value;
}

function initStickyPurchaseBar() {
  const bar = document.querySelector('.sticky-add-to-cart__bar');
  const inputs = stickySizeInputs();
  if (!bar || inputs.length === 0) return;

  setStickyButtonText(bar);

  let wrapper = bar.querySelector('[data-av-sticky-size]');
  let select = wrapper?.querySelector('select');

  if (!wrapper || !select) {
    wrapper = document.createElement('label');
    wrapper.className = 'av-sticky-size';
    wrapper.dataset.avStickySize = '';
    wrapper.innerHTML = '<span>TAGLIA:</span><select aria-label="Scegli la taglia dalla barra acquisto"></select>';
    select = wrapper.querySelector('select');

    const button = bar.querySelector('.sticky-add-to-cart__button');
    bar.insertBefore(wrapper, button || null);
  }

  const signature = inputs.map((input) => `${input.value}:${input.disabled || input.dataset.available === 'false'}`).join('|');
  if (select.dataset.avOptions !== signature) {
    select.replaceChildren(...inputs.map((input) => {
      const option = document.createElement('option');
      option.value = input.value;
      option.textContent = input.value;
      option.disabled = input.disabled || input.dataset.available === 'false';
      return option;
    }));
    select.dataset.avOptions = signature;
  }

  if (select.dataset.avStickyReady !== 'true') {
    select.dataset.avStickyReady = 'true';
    select.addEventListener('change', () => {
      const currentInputs = stickySizeInputs();
      const selectedInput = currentInputs.find((input) => input.value === select.value && !input.disabled);
      selectedInput?.click();
    });
  }

  inputs.forEach((input) => {
    if (input.dataset.avStickyReady === 'true') return;
    input.dataset.avStickyReady = 'true';
    input.addEventListener('change', () => syncStickySize(select, stickySizeInputs()));
  });

  syncStickySize(select, inputs);
}

function updateStickyPurchaseBarVisibility() {
  const bar = document.querySelector('.sticky-add-to-cart__bar');
  const buyButtons = document.querySelector('.product-details .product-form-buttons');
  if (!bar || !buyButtons) return;
  bar.classList.toggle('av-sticky-force-visible', stickyBarShouldForceVisible(buyButtons.getBoundingClientRect().bottom));
}

const stickyVisibilityTargetsBound = [];
function initStickyPurchaseBarVisibility() {
  stickyVisibilityTargets(document, window).forEach((target) => {
    if (stickyVisibilityTargetsBound.includes(target)) return;
    stickyVisibilityTargetsBound.push(target);
    target.addEventListener('scroll', updateStickyPurchaseBarVisibility, { passive: true });
  });
  if (!stickyVisibilityTargetsBound.includes('resize')) {
    stickyVisibilityTargetsBound.push('resize');
    window.addEventListener('resize', updateStickyPurchaseBarVisibility, { passive: true });
  }
  updateStickyPurchaseBarVisibility();
}

let stickyRefreshQueued = false;
function queueStickyPurchaseBarRefresh() {
  if (stickyRefreshQueued) return;
  stickyRefreshQueued = true;
  queueMicrotask(() => {
    stickyRefreshQueued = false;
    initStickyPurchaseBar();
    initStickyPurchaseBarVisibility();
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllPdpCommerce();
      initAllReviewSections();
      initStickyPurchaseBar();
      initStickyPurchaseBarVisibility();
    }, { once: true });
  } else {
    initAllPdpCommerce();
    initAllReviewSections();
    initStickyPurchaseBar();
    initStickyPurchaseBarVisibility();
  }
  document.addEventListener('shopify:section:load', (event) => {
    initAllPdpCommerce(event.target);
    initAllReviewSections(event.target);
    initStickyPurchaseBar();
    initStickyPurchaseBarVisibility();
  });
  new MutationObserver(queueStickyPurchaseBarRefresh).observe(document.documentElement, { childList: true, subtree: true });
}
